import {
  GradientText,
  RightIcon,
  SuccessIcon,
} from "lola-framework-ui-test";
import { useStore } from "../../store/globalState";
import type { TransactionsStatus as status } from "../../utils/commons";
import {
  STATUS_TONE_COLOR,
  toStatusPresentation,
  type StatusTone,
} from "../../utils/statusPresentation";
import {
  StatusCircleIcon,
  StatusFailedIcon,
  StatusPendingIcon,
} from "../../assets/icons";
import { useTranslation } from "react-i18next";

export interface TransactionItemProps {
  status: status;
  amount: string;
  currency: string;
  date: string;
  recipientName?: string;
  cashDepositPending?: boolean;
  onDeposit?: () => void;
  launching?: boolean;
  onSelect?: () => void;
}

/** Initials of first + last name, e.g. "Maria Lopez" -> "ML". */
const getInitials = (name?: string): string => {
  const parts = (name || "").split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
};

const statusIcon = (tone: StatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <StatusFailedIcon size={10} color={color} />;
    case "initial":
      return <StatusPendingIcon size={10} color={color} />;
    case "progress":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

export const TransactionItem: React.FC<TransactionItemProps> = ({
  status,
  amount,
  currency,
  date,
  recipientName,
  cashDepositPending = false,
  onDeposit,
  launching = false,
  onSelect,
}) => {
  const { t } = useTranslation("deferred-origination");
  const { theme } = useStore();
  const { bodyCopy, backgroundBtn, textColorBtn, dropzoneColors } =
    theme.whiteView;

  // Cash deposit awaiting in-person payment: branded tappable CTA (sender only).
  if (cashDepositPending) {
    const accent = dropzoneColors?.[0] ?? textColorBtn;
    const depositName = recipientName || t("transactions.transfers");
    return (
      <div
        className="button-wrapper transactions-deposit-wrapper"
      >
        <button
          type="button"
          className={`transactions-history-item transactions-deposit-item${
            launching ? " deposit-wave" : ""
          }`}
          style={
            {
              background: backgroundBtn,
              "--deposit-avatar-fg": backgroundBtn,
            } as React.CSSProperties
          }
          onClick={onDeposit}
          disabled={launching}
          aria-busy={launching || undefined}
        >
          <span className="transactions-avatar transactions-deposit-avatar">
            {getInitials(recipientName)}
          </span>
          <div className="transactions-deposit-info">
            <div className="transactions-deposit-copy">
              <GradientText className="transaction-title" textColor={textColorBtn}>
                <b>{depositName}</b>
              </GradientText>
              <GradientText
                className="transactions-deposit-amount"
                textColor={textColorBtn}
                as="span"
              >
                ${amount} {currency}
              </GradientText>
            </div>
            <div className="transactions-deposit-action">
              <GradientText
                className="transactions-deposit-cta"
                textColor={textColorBtn}
              >
                {t("transactions.deposit")}
              </GradientText>
              <span
                className="transactions-deposit-arrow"
                style={{ background: accent }}
              >
                <RightIcon size={32} colors={[textColorBtn, textColorBtn]} />
              </span>
            </div>
          </div>
        </button>
      </div>
    );
  }

  const { tone, labelKey } = toStatusPresentation(status);
  const toneColor = STATUS_TONE_COLOR[tone];
  const label = t(labelKey);
  const selectable = typeof onSelect === "function";

  return (
    <section
      className="transactions-history-item"
      onClick={selectable ? onSelect : undefined}
      role={selectable ? "button" : undefined}
      tabIndex={selectable ? 0 : undefined}
      style={selectable ? { cursor: "pointer" } : undefined}
    >
      <p className="transactions-avatar">
        {recipientName ? getInitials(recipientName) : t("transactions.avatar")}
      </p>
      <section className="transactions-info">
        <div className="transactions-row transactions-row--primary">
          <GradientText className="transaction-title" textColor={bodyCopy}>
            <b>{recipientName || t("transactions.transfers")}</b>
          </GradientText>
          <GradientText
            className="transactions-amount"
            textColor={bodyCopy}
            as="span"
          >
            ${amount} {currency}
          </GradientText>
        </div>
        <div className="transactions-row transactions-row--meta">
          <section className="transactions-status">
            {statusIcon(tone, toneColor)}
            <GradientText textColor={toneColor} as="span">
              {label}
            </GradientText>
          </section>
          <GradientText className="transactions-date" textColor={`${bodyCopy}5E`}>
            {date}
          </GradientText>
        </div>
      </section>
    </section>
  );
};
